// REST-based FishService for MySQL backend and Supabase Storage only.
// Firebase has been removed.

import { createClient } from '@supabase/supabase-js';

const COLLECTION_NAME = 'fish'; // This is not used in the REST service, but kept for context.

class FishService {
  // Supabase client for direct storage access (if needed, though API handles it)
  // This is a client-side Supabase instance, not the server-side one.
  // It's primarily for constructing public URLs if the backend only provides paths.
  #supabaseClient = null;
  #supabaseBucket = process.env.REACT_APP_SUPABASE_BUCKET || 'saltfishy'; // Default bucket name
  #apiBase = process.env.REACT_APP_API_BASE || ''; // Empty for relative URLs (proxy in dev)

  constructor() {
    if (process.env.REACT_APP_SUPABASE_URL && process.env.REACT_APP_SUPABASE_ANON_KEY) {
      this.#supabaseClient = createClient(
        process.env.REACT_APP_SUPABASE_URL,
        process.env.REACT_APP_SUPABASE_ANON_KEY
      );
    } else {
      console.warn('Supabase client not initialized in FishService: REACT_APP_SUPABASE_URL or REACT_APP_SUPABASE_ANON_KEY missing.');
    }
  }

  // Helper to construct the full public URL for a Supabase image
  getPublicImageUrl(imagePath) {
    if (!this.#supabaseClient || !imagePath) return imagePath; // Return original path if client not ready or no path
    
    // Check if it's already a full URL
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }

    // Construct the public URL using the client
    const { data } = this.#supabaseClient.storage.from(this.#supabaseBucket).getPublicUrl(imagePath);
    return data?.publicUrl || imagePath; // Fallback to original path if URL not generated
  }

  // Safely parse JSON and surface HTML/error bodies for easier debugging
  async #parseJson(res) {
    const ct = res.headers.get('content-type') || '';
    if (!ct.includes('application/json')) {
      const text = await res.text();
      throw new Error(`Expected JSON, got ${ct || 'unknown'}: ${text.slice(0,200)}`);
    }
    return res.json();
  }

  // Helper to process fish data and ensure image_url is a full public URL
  #processFishData(fishData) {
    if (Array.isArray(fishData)) {
      return fishData.map(fish => ({
        ...fish,
        image_url: this.getPublicImageUrl(fish.image_url)
      }));
    } else if (fishData) {
      return {
        ...fishData,
        image_url: this.getPublicImageUrl(fishData.image_url)
      };
    }
    return fishData;
  }

  // Get all fish
  async getAllFish() {
    const res = await fetch(`${this.#apiBase}/api/fish`);
    if (!res.ok) throw new Error(`Failed to fetch fish: ${res.status} ${res.statusText}`);
    const data = await this.#parseJson(res);
    return this.#processFishData(data);
  }

  // Get fish by ID
  async getFishById(id) {
    const res = await fetch(`${this.#apiBase}/api/fish/${id}`);
    if (res.status === 404) throw new Error('Fish not found');
    if (!res.ok) throw new Error('Failed to fetch fish by id');
    const data = await this.#parseJson(res);
    return this.#processFishData(data);
  }

  // Search fish by category
  async getFishByCategory(category) {
    const res = await fetch(`${this.#apiBase}/api/fish/category/${encodeURIComponent(category)}`);
    if (!res.ok) throw new Error('Failed to fetch by category');
    return await this.#parseJson(res);
  }

  // Search fish by care level
  async getFishByCareLevel(careLevel) {
    const res = await fetch(`${this.#apiBase}/api/fish/care_level/${encodeURIComponent(careLevel)}`);
    if (!res.ok) throw new Error('Failed to fetch by care level');
    return await this.#parseJson(res);
  }

  // Get available fish only
  async getAvailableFish() {
    const res = await fetch(`${this.#apiBase}/api/fish/available`);
    if (!res.ok) throw new Error('Failed to fetch available fish');
    return await this.#parseJson(res);
  }

  // Add new fish
  async addFish(fishData) {
    const res = await fetch(`${this.#apiBase}/api/fish`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(fishData) });
    if (!res.ok) throw new Error('Failed to add fish');
    const data = await this.#parseJson(res);
    return data.id;
  }

  // Update fish
  async updateFish(id, fishData) {
    const res = await fetch(`${this.#apiBase}/api/fish/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(fishData) });
    if (!res.ok) throw new Error('Failed to update fish');
    return id;
  }

  // Delete fish
  async deleteFish(id) {
    const res = await fetch(`${this.#apiBase}/api/fish/${id}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Failed to delete fish');
    return id;
  }

  // Upload fish image
  async uploadFishImage(file, fishId, imageName) {
    const form = new FormData();
    form.append('file', file);
    if (fishId) form.append('fishId', String(fishId));
    if (imageName) form.append('imageName', imageName);
    const res = await fetch(`${this.#apiBase}/api/images/upload`, { method: 'POST', body: form });
    if (!res.ok) throw new Error('Failed to upload image');
    const data = await this.#parseJson(res);
    return data.url;
  }

  // Delete fish image via Supabase (expects a storage key or full public URL)
  async deleteFishImage(imageUrlOrKey) {
    const key = this.#extractSupabaseKey(imageUrlOrKey);
    if (!key) {
      console.warn('deleteFishImage: could not determine storage key from input');
      return false;
    }
    const res = await fetch(`/api/images?key=${encodeURIComponent(key)}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Failed to delete image');
    return true;
  }

  // Private helper to extract Supabase storage key from a public URL or return key as-is
  #extractSupabaseKey(input) {
    if (!input) return null;
    try {
      if (/^https?:\/\//i.test(input)) {
        // Try to parse standard Supabase public URL: .../storage/v1/object/public/{bucket}/{key}
        const u = new URL(input);
        const idx = u.pathname.indexOf('/storage/v1/object/public/');
        if (idx !== -1) {
          const after = u.pathname.substring(idx + '/storage/v1/object/public/'.length);
          const parts = after.split('/');
          // const bucket = parts.shift();
          parts.shift();
          return decodeURIComponent(parts.join('/'));
        }
        // If custom domain mapped directly to bucket root: https://<cdn>/<bucket>/<key>
        const pathParts = u.pathname.replace(/^\//, '').split('/');
        if (pathParts.length > 1) {
          pathParts.shift();
          return decodeURIComponent(pathParts.join('/'));
        }
        return null;
      }
      // Not a URL; assume it's already a key
      return input;
    } catch (_) {
      return null;
    }
  }

  // Real-time listener for fish updates
  subscribeToFishUpdates(callback) {
    // No realtime; provide simple polling every 30s
    let cancelled = false;
    const poll = async () => {
      try {
        const data = await this.getAllFish();
        if (!cancelled) callback(data);
      } catch (e) { /* ignore */ }
      if (!cancelled) timer = setTimeout(poll, 30000);
    };
    let timer = setTimeout(poll, 0);
    return () => { cancelled = true; clearTimeout(timer); };
  }

  // Batch upload fish data
  async batchUploadFish(fishArray) {
    try {
      const uploadPromises = fishArray.map(fish => this.addFish(fish));
      const results = await Promise.all(uploadPromises);
      return results;
    } catch (error) {
      console.error('Error batch uploading fish:', error);
      throw error;
    }
  }
}

export default new FishService();
